/**
 * NotificationsTab — send a push notification to phones from the dashboard.
 *
 * Every admin route needs the key now, so by the time this tab renders the
 * console's unlock screen has already stored one and this gate falls straight
 * through. It stays as the fallback for a key cleared mid-session.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import { styled } from '@mui/system';
import {
    getAudienceCounts,
    sendAdminNotification,
    getNotificationHistory,
    runParkingAlertsNow,
} from '../../services/adminService';
import { ADMIN_KEY_STORAGE as KEY_STORAGE } from '../../services/adminKey';
import { VALET, FONTS, TYPE } from '../../theme';
import { MOBILE_DIALOG } from './responsive';

const Title = styled(Typography)({
    ...TYPE.display,
    fontSize: '22px',
    color: VALET.ink,
    marginBottom: '6px',
});

const Help = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textSecondary,
    lineHeight: 1.6,
});

const Card = styled(Box)({
    background: VALET.surface,
    border: `1px solid ${VALET.border}`,
    borderRadius: '4px',
    padding: '24px',
    boxShadow: VALET.shadow,
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    '@media (max-width: 768px)': {
        padding: '18px 14px',
        gap: '14px',
    },
});

const Reach = styled(Box)({
    fontFamily: FONTS.mono,
    fontSize: '12px',
    letterSpacing: '0.04em',
    color: VALET.bronze,
    background: VALET.bronzeMuted,
    border: `1px solid ${VALET.bronzeHairline}`,
    borderRadius: '3px',
    padding: '10px 12px',
});

const PhonePreview = styled(Box)({
    background: '#1c1c1e',
    borderRadius: '14px',
    padding: '14px 16px',
    color: '#fff',
    fontFamily: FONTS.sans,
    maxWidth: '420px',
});

const AUDIENCES = [
    { value: 'all', label: 'Everyone' },
    { value: 'customers', label: 'Customers only' },
    { value: 'valets', label: 'Valets only' },
];

export default function NotificationsTab() {
    const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
    const [keyDraft, setKeyDraft] = useState('');
    const [unlocked, setUnlocked] = useState(false);

    const [counts, setCounts] = useState(null);
    const [audience, setAudience] = useState('all');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);
    const [toast, setToast] = useState(null);

    const loadAll = useCallback(async (key) => {
        const [c, h] = await Promise.all([
            getAudienceCounts(key),
            getNotificationHistory(key),
        ]);
        setCounts(c.counts);
        setHistory(h.notifications || []);
    }, []);

    useEffect(() => {
        if (!adminKey) return;
        loadAll(adminKey)
            .then(() => setUnlocked(true))
            .catch(() => {
                setUnlocked(false);
                sessionStorage.removeItem(KEY_STORAGE);
                setAdminKey('');
                setToast({ severity: 'error', msg: 'That key was not accepted.' });
            });
    }, [adminKey, loadAll]);

    const reach = useMemo(() => {
        if (!counts) return null;
        return counts[audience] ?? 0;
    }, [counts, audience]);

    const canSend = title.trim() && body.trim() && reach > 0 && !sending;

    const handleUnlock = () => {
        const k = keyDraft.trim();
        if (!k) return;
        sessionStorage.setItem(KEY_STORAGE, k);
        setAdminKey(k);
        setKeyDraft('');
    };

    const handleSend = async () => {
        setConfirmOpen(false);
        setSending(true);
        try {
            const res = await sendAdminNotification(adminKey, {
                audience,
                title: title.trim(),
                body: body.trim(),
            });
            setToast({
                severity: 'success',
                msg: `Sent to ${res.recipientsDelivered} of ${res.recipientsAttempted} phones.`,
            });
            setTitle('');
            setBody('');
            await loadAll(adminKey);
        } catch (err) {
            setToast({ severity: 'error', msg: err.message || 'Send failed.' });
        } finally {
            setSending(false);
        }
    };

    const handleRunAlerts = async () => {
        try {
            const res = await runParkingAlertsNow(adminKey);
            const r = res.result || {};
            setToast({
                severity: 'success',
                msg: `Checked now: ${r.endingSoon || 0} parking-ending, ${r.aspReturn || 0} street-cleaning alerts sent.`,
            });
        } catch (err) {
            setToast({ severity: 'error', msg: err.message || 'Could not run the check.' });
        }
    };

    if (!unlocked) {
        return (
            <Box sx={{ maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Title>Unlock notifications</Title>
                <Help>
                    Sending a notification reaches real phones, so this tab asks for the admin
                    key. It is stored for this browser tab only, and you will need it again
                    after you close the tab.
                </Help>
                <TextField
                    type="password"
                    label="Admin key"
                    value={keyDraft}
                    onChange={(e) => setKeyDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                    size="small"
                    fullWidth
                />
                <Box>
                    <Button
                        variant="contained"
                        onClick={handleUnlock}
                        disabled={!keyDraft.trim()}
                        sx={{
                            background: VALET.green,
                            borderRadius: 0,
                            textTransform: 'none',
                            '&:hover': { background: VALET.greenDark },
                        }}
                    >
                        Unlock
                    </Button>
                </Box>
                <Snackbar
                    open={!!toast}
                    autoHideDuration={5000}
                    onClose={() => setToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={toast?.severity || 'info'}>{toast?.msg}</Alert>
                </Snackbar>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 900 }}>
            <Card>
                <Box>
                    <Title>Send a notification</Title>
                    <Help>
                        This arrives on the phone even when the app is closed. Keep it short —
                        phones cut off long messages.
                    </Help>
                </Box>

                <TextField
                    select
                    label="Who gets it"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    size="small"
                    sx={{ maxWidth: 280 }}
                >
                    {AUDIENCES.map((a) => (
                        <MenuItem key={a.value} value={a.value}>
                            {a.label}
                            {counts ? ` — ${counts[a.value] ?? 0} phones` : ''}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 120 }}
                    helperText={`${title.length}/120`}
                    fullWidth
                />

                <TextField
                    label="Message"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    size="small"
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${body.length}/500`}
                    fullWidth
                />

                {(title || body) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Help>How it looks on a phone</Help>
                        <PhonePreview>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {title || 'Title'}
                            </Typography>
                            <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                                {body || 'Message'}
                            </Typography>
                        </PhonePreview>
                    </Box>
                )}

                <Reach>
                    {reach === null
                        ? 'Counting phones…'
                        : `This reaches ${reach} phone${reach === 1 ? '' : 's'} right now.`}
                </Reach>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        disabled={!canSend}
                        onClick={() => setConfirmOpen(true)}
                        sx={{
                            background: VALET.green,
                            borderRadius: 0,
                            textTransform: 'none',
                            '&:hover': { background: VALET.greenDark },
                        }}
                    >
                        {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send'}
                    </Button>
                    <Help>You will get one more chance to confirm.</Help>
                </Box>
            </Card>

            <Card>
                <Box>
                    <Title>Parking and street-cleaning alerts</Title>
                    <Help>
                        These go out on their own: 15 minutes before a customer's parking runs
                        out, and 10 minutes before their car goes back for street cleaning. The
                        server checks every minute. Use this button only to check it by hand.
                    </Help>
                </Box>
                <Box>
                    <Button
                        variant="outlined"
                        onClick={handleRunAlerts}
                        sx={{
                            borderColor: VALET.borderStrong,
                            color: VALET.ink,
                            borderRadius: 0,
                            textTransform: 'none',
                        }}
                    >
                        Check now
                    </Button>
                </Box>
            </Card>

            <Card>
                <Title>What has been sent</Title>
                {history.length === 0 ? (
                    <Help>Nothing sent from the dashboard yet.</Help>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Who</TableCell>
                                    <TableCell>Message</TableCell>
                                    <TableCell align="right">Reached</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((h) => (
                                    <TableRow key={h._id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {new Date(h.sentAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell>{h.audience}</TableCell>
                                        <TableCell>
                                            <strong>{h.title}</strong>
                                            <br />
                                            {h.body}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: FONTS.mono }}>
                                            {h.recipientsDelivered}/{h.recipientsAttempted}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                sx={MOBILE_DIALOG}
            >
                <DialogTitle sx={{ ...TYPE.display, fontSize: 20 }}>
                    Send to {reach} phone{reach === 1 ? '' : 's'}?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: FONTS.sans, fontSize: 14 }}>
                        This buzzes {reach} real phone{reach === 1 ? '' : 's'} straight away and
                        cannot be taken back.
                    </DialogContentText>
                    <PhonePreview sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
                        <Typography sx={{ fontSize: 13, opacity: 0.85 }}>{body}</Typography>
                    </PhonePreview>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{ textTransform: 'none', color: VALET.textSecondary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        variant="contained"
                        sx={{
                            background: VALET.green,
                            borderRadius: 0,
                            textTransform: 'none',
                            '&:hover': { background: VALET.greenDark },
                        }}
                    >
                        Send it
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!toast}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={toast?.severity || 'info'}>{toast?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
